/**
 * Chaos-meter scoreboard (v9-interactive). Tracks cumulative session counters:
 * payments settled, fraud blocked, sanctions caught, balance conserved. Pops a
 * transient milestone badge when a counter crosses a notable threshold.
 */

import type { RequestEvent } from './feed.js';

interface Counters {
  settled: number;
  fraudBlocked: number;
  sanctionsCaught: number;
}

const MONEY_SCENARIOS = new Set(['sct', 'sct_inst', 'internal_transfer', 'fx_convert', 'p2p_redeem']);

const prev: Counters = { settled: 0, fraudBlocked: 0, sanctionsCaught: 0 };
const current: Counters = { settled: 0, fraudBlocked: 0, sanctionsCaught: 0 };
const seenSeqs = new Set<number>();

const MILESTONES: Array<{ counter: keyof Counters; at: number; label: string }> = [
  { counter: 'settled', at: 1, label: '🎉 First payment settled!' },
  { counter: 'settled', at: 10, label: '✅ 10 payments settled' },
  { counter: 'settled', at: 50, label: '💪 50 payments settled' },
  { counter: 'settled', at: 100, label: '🚀 100 payments settled!' },
  { counter: 'fraudBlocked', at: 1, label: '🛡 First fraud attempt blocked!' },
  { counter: 'fraudBlocked', at: 5, label: '🛡 5 fraud attempts blocked' },
  { counter: 'sanctionsCaught', at: 1, label: '⛔ Sanctions watchlist hit!' },
  { counter: 'sanctionsCaught', at: 3, label: '⛔ 3 sanctions catches' },
];
const firedMilestones = new Set<string>();

export function resetScoreboard(): void {
  prev.settled = 0; prev.fraudBlocked = 0; prev.sanctionsCaught = 0;
  current.settled = 0; current.fraudBlocked = 0; current.sanctionsCaught = 0;
  seenSeqs.clear();
  firedMilestones.clear();
}

function isFraudBlock(e: RequestEvent): boolean {
  return e.scenario === 'fraud_check' && (e.outcome !== 'success' || e.httpStatus === 403);
}
function isSanctionsHit(e: RequestEvent): boolean {
  return (e.scenario === 'sanctions_screen' || e.scenario === 'sanctions_payment' || e.scenario === 'fraud_check')
    && (e.outcome !== 'success' && (e.httpStatus === 403 || (e.detail?.includes('MATCH') ?? false) || (e.detail?.includes('BLOCK') ?? false)));
}

export function updateScoreboard(doc: Document, events: RequestEvent[], conservedOk: boolean): void {
  for (const e of events) {
    if (seenSeqs.has(e.seq)) continue;
    seenSeqs.add(e.seq);
    // A declined journey made no call: it settled no payment, blocked no fraud, caught no
    // sanction. Counting it here previously ticked the "payments settled" odometer for transfers
    // that never left the simulator.
    if (e.skipped) continue;
    if (MONEY_SCENARIOS.has(e.scenario) && e.outcome === 'success') current.settled++;
    if (isFraudBlock(e)) current.fraudBlocked++;
    if (isSanctionsHit(e)) current.sanctionsCaught++;
  }

  setOdometer(doc, '[data-score="settled"]', current.settled, prev.settled);
  setOdometer(doc, '[data-score="fraud"]', current.fraudBlocked, prev.fraudBlocked);
  setOdometer(doc, '[data-score="sanctions"]', current.sanctionsCaught, prev.sanctionsCaught);

  // Conservation indicator
  const conserveEl = doc.querySelector('[data-score="conserved"]');
  if (conserveEl) {
    conserveEl.textContent = conservedOk ? '✓' : '~';
    conserveEl.setAttribute('data-ok', conservedOk ? 'true' : 'false');
  }

  checkMilestones(doc);
  prev.settled = current.settled;
  prev.fraudBlocked = current.fraudBlocked;
  prev.sanctionsCaught = current.sanctionsCaught;
}

function setOdometer(doc: Document, sel: string, val: number, prevVal: number): void {
  const el = doc.querySelector(sel);
  if (!el) return;
  if (val !== prevVal) {
    el.setAttribute('data-rolling', 'true');
    window.setTimeout(() => el.removeAttribute('data-rolling'), 400);
  }
  el.textContent = String(val);
}

function checkMilestones(doc: Document): void {
  for (const m of MILESTONES) {
    const key = `${m.counter}:${m.at}`;
    if (!firedMilestones.has(key) && current[m.counter] >= m.at) {
      firedMilestones.add(key);
      popBadge(doc, m.label);
    }
  }
}

function popBadge(doc: Document, label: string): void {
  const badge = doc.createElement('div');
  badge.className = 'milestone-badge';
  badge.textContent = label;
  doc.body.append(badge);
  // trigger CSS animation then remove
  window.requestAnimationFrame(() => {
    badge.setAttribute('data-visible', 'true');
    window.setTimeout(() => {
      badge.setAttribute('data-visible', 'false');
      window.setTimeout(() => badge.remove(), 500);
    }, 3000);
  });
}

export function getCounters(): Readonly<Counters> {
  return { ...current };
}
